import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import { usePhotoSeriesGet } from "hooks/citajteo/photo_series/usePhotoSeriesGet";
import { usePhotoSeriesUpdate } from "hooks/citajteo/photo_series/usePhotoSeriesUpdate";
import { usePhotoSeriesAdd } from "hooks/citajteo/photo_series/usePhotoSeriesAdd";
import { QUERY_KEYS } from "constant/react_query";
import toast from "react-hot-toast";
import { PhotoSeries } from "model/photo_series/PhotoSeries";

type PhotoSeriesFormState = {
  name: string;
  slug: string;
  desc: string;
  group: string;
  sType: string;
  sOrd: string;
};

const EMPTY_FORM: PhotoSeriesFormState = {
  name: "",
  slug: "",
  desc: "",
  group: "",
  sType: "fotografije",
  sOrd: "0",
};

const buildFormFromPhotoSeries = (
  photoSeries: PhotoSeries,
): PhotoSeriesFormState => ({
  name: photoSeries.name ?? "",
  slug: photoSeries.slug ?? "",
  desc: photoSeries.desc ?? "",
  group: photoSeries.group ?? "",
  sType: photoSeries.sType ?? "fotografije",
  sOrd: String(photoSeries.sOrd ?? 0),
});

const PhotoSeriesEdit: React.FC = () => {
  const navigate = useNavigate();
  const { itemId = "" } = useParams<{ itemId?: string }>();
  const photoSeries = usePhotoSeriesGet(itemId);
  const { photoSeriesUpdateMutate } = usePhotoSeriesUpdate({
    invalidateKeysArray: [
      [QUERY_KEYS.CITAJTEO_PHOTO_SERIES_SEARCH],
      [QUERY_KEYS.CITAJTEO_PHOTO_SERIES_GET, itemId],
    ],
  });
  const { photoSeriesAddMutate } = usePhotoSeriesAdd({
    invalidateKeys: [QUERY_KEYS.CITAJTEO_PHOTO_SERIES_SEARCH],
  });
  const [form, setForm] = useState<PhotoSeriesFormState>(EMPTY_FORM);
  const [errorText, setErrorText] = useState("");

  useEffect(() => {
    if (photoSeries) {
      setForm(buildFormFromPhotoSeries(photoSeries));
    }
  }, [photoSeries]);

  const handleChange =
    (field: keyof PhotoSeriesFormState) =>
    (
      event: React.ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >,
    ) => {
      setForm((currentForm) => ({
        ...currentForm,
        [field]: event.target.value,
      }));
    };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorText("");

    const trimmedName = form.name.trim();
    const trimmedSlug = form.slug.trim();
    const trimmedGroup = form.group.trim();
    const trimmedSType = form.sType.trim();
    const trimmedDesc = form.desc.trim();
    const parsedSOrd = Number.parseInt(form.sOrd, 10);

    if (trimmedName === "") {
      setErrorText("Naziv ne sme biti prazan.");
      return;
    }

    if (trimmedGroup === "") {
      setErrorText("Grupa ne sme biti prazna.");
      return;
    }

    if (trimmedSType === "") {
      setErrorText("Tip ne sme biti prazan.");
      return;
    }

    if (Number.isNaN(parsedSOrd)) {
      setErrorText("Redni broj mora biti broj.");
      return;
    }

    const updatePayload = {
      name: trimmedName,
      slug: trimmedSlug,
      desc: trimmedDesc,
      group: trimmedGroup,
      sType: trimmedSType,
      sOrd: parsedSOrd,
      photos: photoSeries?.photos ?? [],
      represent: photoSeries?.represent ?? null,
    };

    if (itemId !== "") {
      photoSeriesUpdateMutate(
        { id: itemId, updatePayload },
        {
          onSuccess: () => {
            toast.success("Uspešno ažuriranje foto serije.");
            navigate("/admin-dashboard/photo-ser-li");
          },
          onError: (error) => {
            console.error("Error updating photo series:", error);
            toast.error("Greška prilikom ažuriranja foto serije.");
          },
        },
      );
      return;
    }

    photoSeriesAddMutate(updatePayload, {
      onSuccess: () => {
        toast.success("Uspešno dodavanje foto serije.");
        navigate("/admin-dashboard/photo-ser-li");
      },
      onError: (error) => {
        console.error("Error adding photo series:", error);
        toast.error("Greška prilikom dodavanja foto serije.");
      },
    });
  };

  const handleReset = () => {
    if (photoSeries) {
      setForm(buildFormFromPhotoSeries(photoSeries));
    } else {
      setForm(EMPTY_FORM);
    }
    setErrorText("");
  };

  if (itemId !== "" && !photoSeries) {
    return <FullWidth>Učitavanje foto serije...</FullWidth>;
  }

  return (
    <FullWidth>
      <div className="App">
        <Form onSubmit={handleSubmit}>
          <Title>
            {itemId !== "" ? "Uredi foto seriju" : "Nova foto serija"}
          </Title>

          <Field>
            <label htmlFor="name">Naziv</label>
            <Input
              id="name"
              type="text"
              value={form.name}
              onChange={handleChange("name")}
            />
          </Field>

          <Field>
            <label htmlFor="slug">Slug</label>
            <Input
              id="slug"
              type="text"
              value={form.slug}
              onChange={handleChange("slug")}
            />
          </Field>

          <Field>
            <label htmlFor="group">Grupa</label>
            <Input
              id="group"
              type="text"
              value={form.group}
              onChange={handleChange("group")}
              placeholder="slug odrednice"
            />
          </Field>

          <Field>
            <label htmlFor="sType">Tip</label>
            <Select
              id="sType"
              value={form.sType}
              onChange={handleChange("sType")}
            >
              <option value="fotografije">fotografije</option>
              <option value="neknjižna građa">neknjižna građa</option>
            </Select>
          </Field>

          <Field>
            <label htmlFor="sOrd">Redni broj</label>
            <Input
              id="sOrd"
              type="number"
              value={form.sOrd}
              onChange={handleChange("sOrd")}
            />
          </Field>

          <Field>
            <label htmlFor="desc">Opis</label>
            <TextArea
              id="desc"
              rows={8}
              value={form.desc}
              onChange={handleChange("desc")}
            />
          </Field>

          {errorText !== "" && <ErrorText>{errorText}</ErrorText>}

          <Actions>
            <Button type="submit">OK</Button>
            <Button type="button" onClick={handleReset}>
              Reset
            </Button>
          </Actions>
        </Form>
      </div>
    </FullWidth>
  );
};

export default PhotoSeriesEdit;

const FullWidth = styled.div`
  width: 100%;
`;

const Form = styled.form`
  width: 100%;
  max-width: 900px;
`;

const Title = styled.h2`
  margin: 0 0 1rem;
`;

const Field = styled.div`
  margin-bottom: 1rem;

  label {
    display: block;
    margin-bottom: 0.35rem;
    font-weight: 600;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.5rem 0.65rem;
`;

const Select = styled.select`
  width: 100%;
  padding: 0.5rem 0.65rem;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.5rem 0.65rem;
  resize: vertical;
`;

const ErrorText = styled.div`
  margin-bottom: 1rem;
  color: #b00020;
  font-weight: 600;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const Button = styled.button`
  padding: 0.4rem 0.9rem;
  cursor: pointer;
`;
